import React from 'react';
import { TextField } from '@mui/material';

import { LLMModelInvocationProfileInput } from '../../../types/admin';

export interface ProfileConfigFieldProps {
  profile: LLMModelInvocationProfileInput;
  onUpdate: (patch: Partial<LLMModelInvocationProfileInput>) => void;
}

const FALLBACK_KEY = 'toolCallExtrasFallback';

type JsonObject = Record<string, unknown>;

const parseObject = (text: string | null | undefined): JsonObject => {
  if (!text?.trim()) return {};
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as JsonObject)
      : {};
  } catch {
    return {};
  }
};

const isJsonObject = (text: string): boolean => {
  try {
    const parsed = JSON.parse(text);
    return Boolean(parsed) && typeof parsed === 'object' && !Array.isArray(parsed);
  } catch {
    return false;
  }
};

/**
 * Typed editor for the only `LLMModelInvocationProfile.config` key a runtime reader uses:
 * `toolCallExtrasFallback` (wire fields sent for replayed tool calls the model did not produce).
 * Any other key already stored on the profile is preserved on save.
 */
export const ProfileConfigField: React.FC<ProfileConfigFieldProps> = ({ profile, onUpdate }) => {
  const initial = parseObject(profile.config)[FALLBACK_KEY];
  const [text, setText] = React.useState(
    initial === undefined ? '' : JSON.stringify(initial, null, 2)
  );
  const invalid = text.trim() !== '' && !isJsonObject(text);

  const write = (next: string) => {
    const config = parseObject(profile.config);
    if (next.trim() === '') delete config[FALLBACK_KEY];
    else config[FALLBACK_KEY] = JSON.parse(next);
    onUpdate({ config: Object.keys(config).length > 0 ? JSON.stringify(config) : null });
  };

  return (
    <TextField
      size="small"
      fullWidth
      multiline
      minRows={2}
      label="Tool-call extras fallback (JSON)"
      value={text}
      error={invalid}
      helperText={
        invalid
          ? 'Must be a JSON object.'
          : 'Optional. Wire fields added to tool calls this model did not produce, e.g. a signature placeholder for Gemini.'
      }
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        if (next.trim() === '' || isJsonObject(next)) write(next);
      }}
      inputProps={{ className: 'font-mono text-xs' }}
    />
  );
};
